import React, { lazy, Suspense, useEffect } from 'react';
import { render } from 'react-dom';
import { Provider } from 'react-redux';
import * as Sentry from '@sentry/react';
import './vendor/modernizr';
import mitt from 'mitt';
import { AuthProvider } from './Context/AuthProvider';
import { store } from './storeAndHistory';
import App from './App';
import './App.scss';
import './Fonts.scss';
import {
  AuthPlatform,
  beginAuthWorkflow,
  checkStorageForRefreshToken,
  checkStorageForTemporaryAccess,
  clearJWTToken,
  completeAuthWorkflow,
  completeEmailAuthWorkflow,
  getOrRefreshAccessToken,
  handleAuthorizationError,
  handoffTokenToDesktop,
  isAccessTokenValid,
  isRefreshTokenValid,
  refreshAuth,
  setNewMarketingSignUp,
  UnauthorizedError,
} from '@support/jwt';
import { lastValueFrom } from 'rxjs';
import { appVersion, sentryKey } from '@app/config';
import { getRewardfulAffiliateCode, storeNewUserUTMQuery } from '@support/utm';
import { stopKeyboardEventBubbling, targetIsTextInput } from '@support/keyboardHandler';
import { desktopRequiresUpdate } from '@support/version-check';
import { apiRequest } from '@support/api';
import { WorkspaceProvider } from './Context/WorkspaceProvider';
import { PostHogProvider } from 'posthog-js/react';
import deviceIntegrationStateDispatch from '@actions/deviceIntegrations';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

const UpdateRequired = lazy(() => import('@views/UpdateRequired'));

const consoleError = console.error;
const SUPPRESSED_ERRORS = ['A component is `contentEditable` and contains `children`'];
console.error = function filterErrors(err: any, ...args) {
  if (!SUPPRESSED_ERRORS.some((entry) => err?.includes?.(entry))) {
    consoleError(err, ...args);
  }
};

const getEnv = () => {
  switch (location.host) {
    case 'web.clay.earth':
      return 'production';

    case 'web-staging.clay.earth':
      return 'staging';

    case 'web-spa.clay.technology':
      return 'staging';

    default:
      return 'development';
  }
};
window.getEnv = getEnv;

window.app = {
  confirm: (_opts, _callback) => {},
  isElectron: navigator.userAgent.includes('Electron'),
  ...mitt(),
};

if (sentryKey) {
  Sentry.init({
    dsn: sentryKey,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
    ],
    tracesSampleRate: 0.2, // log 20% of transactions,
    environment: getEnv(),
    release: appVersion,
  });
  if (window.app.isElectron && window.desktopIntegrations) {
    Sentry.setTag('desktop-version', window.desktopIntegrations.appInfo.version);

    if (window.desktopIntegrations.setScopes) {
      window.desktopIntegrations.setScopes({
        tags: {
          'web-version': appVersion,
          'web-env': getEnv(),
        },
      });
    }
  }
}

function startApp() {
  if (desktopRequiresUpdate()) {
    render(
      <Suspense fallback={null}>
        <UpdateRequired />
      </Suspense>,
      document.getElementById('root'),
    );
    return;
  }

  render(
    <Provider store={store}>
      <PostHogProvider
        apiKey="phc_e3LUt97M4IHVNZzwQfrkyb2LIIqiIqXz2FaqJ39jLig"
        options={{
          api_host: 'https://app.posthog.com',
          autocapture: false,
          disable_session_recording: true,
          session_recording: { maskAllInputs: true },
        }}
      >
        <AuthProvider>
          <WorkspaceProvider>
            <App />
          </WorkspaceProvider>
        </AuthProvider>
      </PostHogProvider>
    </Provider>,
    document.getElementById('root'),
  );
}

window.addEventListener('keydown', (e: KeyboardEvent) => {
  window.app.emit('keydown:all', e);

  if (targetIsTextInput(e) && stopKeyboardEventBubbling(e)) {
    return;
  }

  window.app.emit('keydown', e);

  if (window.__activeAction || e.defaultPrevented) {
    return;
  }

  // Prioritized hotkey firing, to be used if we have shared bindings
  // within a single view. Can include higher priorities as needed.
  window.app.emit('hotkey_2', e);

  if (!e.defaultPrevented) {
    window.app.emit('hotkey_1', e);
  }

  if (!e.defaultPrevented) {
    window.app.emit('hotkey', e);
  }
});

document.addEventListener('DOMContentLoaded', async () => {
  if (navigator.userAgent.indexOf('Firefox') > -1) {
    document.body.classList.add('firefox');
  }

  if (!!window.safari) {
    document.body.classList.add('safari');
  }

  if (window.app.isElectron) {
    document.body.classList.add('electron');
    if (window.desktopIntegrations) {
      document.body.classList.add(window.desktopIntegrations.appInfo.os.toLowerCase());
      deviceIntegrationStateDispatch(store.dispatch);
    }

    // Used to provide the main electron process with the auth token
    if (window.desktopIntegrations && window.desktopIntegrations.getOrRefreshAuthHandler) {
      window.desktopIntegrations.getOrRefreshAuthHandler(async () => {
        try {
          return await lastValueFrom(getOrRefreshAccessToken());
        } catch (e) {
          if (e instanceof UnauthorizedError) {
            // Actual error object cannot be passed to the main process, so simplifying to a
            // simple message.
            throw new Error('Unauthorized');
          }
          throw e;
        }
      });
    }
  }

  // if we already have a valid JWT, use it!
  if (isAccessTokenValid()) {
    startApp();
    handoffTokenToDesktop();
    return;
  } else if (isRefreshTokenValid()) {
    await lastValueFrom(refreshAuth(checkStorageForRefreshToken()!));
    startApp();
    return;
  } else {
    // clear both
    clearJWTToken();
  }

  const queryParams = new URLSearchParams(window.location.search);

  if (queryParams.get('frommarketing')) {
    const platform = queryParams.get('frommarketing');
    const referralCode = queryParams.get('referral') || undefined;

    if (platform === 'apple' || platform === 'google') {
      try {
        storeNewUserUTMQuery();
        getRewardfulAffiliateCode();
        const { redirect } = await lastValueFrom(beginAuthWorkflow(platform, true, referralCode));
        window.location.href = redirect;
        return;
      } catch (err) {
        console.error(err);
      }
    } else if (
      platform === 'email' &&
      queryParams.has('email') &&
      !queryParams.has('marketing_email_signup_started')
    ) {
      const email = decodeURIComponent(queryParams.get('email'));
      // We start the email signup process here, then use search params to initialize the login
      // view with the correct state.
      try {
        storeNewUserUTMQuery();
        getRewardfulAffiliateCode();
        setNewMarketingSignUp('email');
        const signup = await apiRequest<{ email: string }>(
          'POST',
          '/auth/email-signup/',
          { email, referral_code: referralCode },
          { apiVersion: 'v2', noAuthorization: true },
        );

        queryParams.set('marketing_email_signup_started', 'true');
        queryParams.set('email', encodeURIComponent(signup.email));
        window.location.search = queryParams.toString();
        startApp();
        return;
      } catch (err) {
        console.error(err);
        startApp();
        return;
      }
    } else if (queryParams.has('marketing_email_signup_started')) {
      startApp();
      return;
    } else {
      window.location.href = '/';
      return;
    }
  } else if (
    queryParams.get('type') === 'email' &&
    queryParams.has('token') &&
    queryParams.has('email') &&
    !window.location.pathname.includes('open-in-desktop')
  ) {
    try {
      await lastValueFrom(
        completeEmailAuthWorkflow(
          queryParams.get('token'),
          window.atob(queryParams.get('email')),
          queryParams.get('register') === '1',
        ),
      );
      startApp();
      return;
    } catch (err) {
      console.error(err);
    }
  }

  // check for login progress
  const access = checkStorageForTemporaryAccess();
  if (!access) {
    startApp();
    return;
  }

  // check query params for login error
  if (queryParams.get('error')) {
    handleAuthorizationError(queryParams.get('error'), queryParams.get('platform') as AuthPlatform);
    startApp();
    return;
  }

  // take the token and get API JWT
  try {
    await lastValueFrom(completeAuthWorkflow(access.token, access.provider));
    startApp();
  } catch (err) {
    window.location.replace('/login');
  }
});

// We have some global event handling that is facilitated outside of components. In order
// to dispatch these updates through to the store, we need to get the running store instance
export { store };
